import type {
  CapabilityOverride,
  CapabilityOverrideWriteResult,
  CapabilityScopeType,
  CreateCapabilityOverrideRequest,
} from "@/services/capabilitiesApi";
import type { TripwirePin } from "@/utils/tripwireBadge";

export const OVERRIDE_REMOVED_THEN_FAILED =
  "The previous override was removed, but the new one was not saved. Tripwire is inheriting until you retry.";

export class OverrideRemovedThenFailedError extends Error {
  constructor() {
    super(OVERRIDE_REMOVED_THEN_FAILED);
    this.name = "OverrideRemovedThenFailedError";
  }
}

// A row and the desired pin represent the same decision when both the state
// and the expiry match. Without checking expiry too, extending or clearing a
// pause by editing only its expires_at would look like a no-op and never
// reach the API.
function sameDecision(
  current: CapabilityOverride | undefined,
  desired: TripwirePin,
  expiresAt: string | null,
): boolean {
  if (!current) return false;
  return current.state === desired && current.expires_at === expiresAt;
}

export async function syncCapabilityPin(args: {
  capability: string;
  scopeType: CapabilityScopeType;
  scopeId: string;
  current: CapabilityOverride | undefined;
  desired: TripwirePin;
  reason: string;
  expiresAt?: string | null;
  create: (
    body: CreateCapabilityOverrideRequest,
  ) => Promise<CapabilityOverrideWriteResult>;
  remove: (overrideId: number) => Promise<void>;
  onUnchanged?: () => void;
}): Promise<boolean> {
  const { current, desired } = args;
  if (desired === "inherit") {
    if (!current) return false;
    await args.remove(current.id);
    return true;
  }
  if (sameDecision(current, desired, args.expiresAt ?? null)) return false;

  const write = async () => {
    const result = await args.create({
      capability: args.capability,
      scope_type: args.scopeType,
      scope_id: args.scopeId,
      state: desired,
      reason: args.reason,
      ...(args.expiresAt !== undefined ? { expires_at: args.expiresAt } : {}),
    });
    if (!result.changed) {
      args.onUnchanged?.();
    }
  };

  if (current) {
    await args.remove(current.id);
    try {
      await write();
    } catch {
      throw new OverrideRemovedThenFailedError();
    }
    return true;
  }
  await write();
  return true;
}
